// Visor de conteo rápido: estimación en vivo con las actas de una muestra de mesas.
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Data, Layout } from "plotly.js-dist-min";
import { ConteoAPI, ConteoMesa, ConteoVista } from "../api/endpoints";
import ProjectFormPicker from "../components/ProjectFormPicker";
import PlotlyChart, { COLORWAY } from "../components/Plot";
import { Card, ErrorBanner, HelpBox, InfoBanner, OkBanner, PageHeader, Spinner } from "../components/Layout";

const REFRESCO = [
  { v: 0, label: "Sin actualización automática" },
  { v: 30, label: "Cada 30 segundos" },
  { v: 60, label: "Cada 1 minuto" },
  { v: 120, label: "Cada 2 minutos" },
];
const FUENTE: Record<string, string> = { "1": "Conteo", "2": "Acta", "3": "Acta observada" };

const pct = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${v.toFixed(1)} %`);
const mg = (v: number | null | undefined) => (v === null || v === undefined ? "" : ` ±${v.toFixed(1)}`);

function Tile({ titulo, valor, nota }: { titulo: string; valor: string; nota?: string }) {
  return (
    <div className="tarjeta">
      <div className="etiqueta">{titulo}</div>
      <div className="text-2xl font-bold text-tinta">{valor}</div>
      {nota && <div className="text-xs text-tinta-3 mt-1">{nota}</div>}
    </div>
  );
}

function GraficoPartidos({ res, base }: { res: ConteoVista; base: "validos" | "emitidos" }) {
  const filas = res.resultados.filter((f) => (base === "validos" ? f.pct_validos !== null : f.pct_emitidos !== null)).slice(0, 10).reverse();
  const x = filas.map((f) => (base === "validos" ? f.pct_validos : f.pct_emitidos) ?? 0);
  const err = filas.map((f) => (base === "validos" ? f.me_validos : f.me_emitidos) ?? 0);
  const data: Data[] = [{
    type: "bar", orientation: "h", y: filas.map((f) => f.partido), x,
    error_x: { type: "data", array: err, visible: true, color: "#555" },
    text: x.map((v) => `${v.toFixed(1)} %`), textposition: "outside", marker: { color: COLORWAY[0] },
    hovertemplate: "%{y}: %{x:.1f} %<extra></extra>",
  }];
  const layout: Partial<Layout> = {
    height: 60 + filas.length * 34, margin: { l: 210, r: 60, t: 10, b: 30 },
    xaxis: { ticksuffix: " %", rangemode: "tozero" }, showlegend: false,
  };
  return <PlotlyChart data={data} layout={layout} />;
}

function GraficoEvolucion({ res }: { res: ConteoVista }) {
  if (!res.diferencia || res.evolucion.length < 2) return null;
  const { a, b } = res.diferencia;
  const xs = res.evolucion.map((e) => e.actas);
  const data: Data[] = [a, b].map((p, i) => ({
    type: "scatter", mode: "lines+markers", name: p, x: xs, y: res.evolucion.map((e) => e[p] as number),
    line: { color: COLORWAY[i] }, hovertemplate: `${p}: %{y:.1f} %<br>%{x} actas<extra></extra>`,
  }));
  const layout: Partial<Layout> = {
    height: 280, margin: { l: 50, r: 20, t: 10, b: 40 }, legend: { orientation: "h", y: -0.25 },
    xaxis: { title: { text: "Actas recibidas" } }, yaxis: { ticksuffix: " %" },
  };
  return <PlotlyChart data={data} layout={layout} />;
}

export default function ConteoRapidoPage() {
  const qc = useQueryClient();
  const [projectId, setProjectId] = useState<number | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [refresco, setRefresco] = useState<number>(60);
  const [base, setBase] = useState<"validos" | "emitidos">("validos");
  const [aviso, setAviso] = useState("");
  const [previa, setPrevia] = useState<{ mesas: ConteoMesa[]; titulares: number; reservas: number } | null>(null);
  const [errorCsv, setErrorCsv] = useState("");
  const [totalMesas, setTotalMesas] = useState<string>("");
  const [verPendientes, setVerPendientes] = useState(false);
  const [modo, setModo] = useState<"muestra" | "todas">("muestra");

  const listo = !!projectId && !!formId;
  const cfgQ = useQuery({ queryKey: ["conteo-config", projectId, formId], queryFn: () => ConteoAPI.config(projectId!, formId!), enabled: listo });
  const cfg = cfgQ.data;
  const configurado = !!cfg && cfg.mesas.length > 0;
  const resQ = useQuery({
    queryKey: ["conteo-resultados", projectId, formId],
    queryFn: () => ConteoAPI.resultados(projectId!, formId!),
    enabled: listo && configurado,
    refetchInterval: refresco > 0 ? refresco * 1000 : false,
  });
  const res = resQ.data;
  const leerAhora = useMutation({
    mutationFn: () => ConteoAPI.resultados(projectId!, formId!, true),
    onSuccess: (data) => {
      qc.setQueryData(["conteo-resultados", projectId, formId], data);
      setAviso(`Leído de ODK a las ${data.resumen.calculado}.`);
    },
  });
  const guardar = useMutation({
    mutationFn: (body: { options?: { total_mesas: number }; mesas?: ConteoMesa[] }) => ConteoAPI.guardar(projectId!, formId!, body),
    onSuccess: () => {
      setAviso("Configuración guardada.");
      setPrevia(null);
      void qc.invalidateQueries({ queryKey: ["conteo-config", projectId, formId] });
      void qc.invalidateQueries({ queryKey: ["conteo-resultados", projectId, formId] });
    },
  });
  const leerCsv = useMutation({
    mutationFn: (texto: string) => ConteoAPI.leerMuestra(texto),
    onSuccess: (r) => { setPrevia(r); setErrorCsv(""); },
    onError: (e: Error) => { setPrevia(null); setErrorCsv(e.message); },
  });

  const vista: ConteoVista | null = res ? (modo === "todas" ? res.todas : res) : null;
  const mesasOrdenadas = useMemo(() => [...(res?.mesas ?? [])].sort((x, y) => String(y.hora).localeCompare(String(x.hora))), [res]);
  const dif = vista?.diferencia;
  const pa = dif?.a ?? "";
  const pb = dif?.b ?? "";

  return (
    <div className="space-y-4">
      <PageHeader title="Conteo rápido" subtitle="Estimación en vivo con las actas de una muestra probabilística de mesas" />
      <HelpBox>
        <p><b>Qué muestra.</b> Cada envío del formulario es <b>una mesa</b>: los votos de cada partido, blancos, nulos y el
          total de votos emitidos. Con la muestra de mesas cargada, el visor estima el resultado del distrito y su margen.</p>
        <p><b>Reglas.</b> Si una mesa llega dos veces, vale el <b>último envío</b>. La reserva ocupa el lugar de su titular.
          Las mesas que <b>no</b> están en la muestra se informan aparte y no entran en la estimación. Las actas con menos de
          100 votos se marcan como sospechosas: suelen ser conteos a medias.</p>
        <p><b>Cobertura.</b> Mire cuántas actas llegaron de cada zona: si una zona viene atrasada, el parcial puede estar
          sesgado aunque el margen sea chico.</p>
      </HelpBox>

      <Card>
        <ProjectFormPicker projectId={projectId} formId={formId}
          onProject={(id) => { setProjectId(id); setFormId(null); }} onForm={setFormId} />
      </Card>

      {aviso && <OkBanner message={aviso} />}
      {cfgQ.error && <ErrorBanner message={(cfgQ.error as Error).message} />}

      {listo && cfg && (cfg.puede_configurar || !configurado) && (
        <Card>
          <div className="etiqueta mb-2">Muestra de mesas</div>
          {configurado
            ? <p className="text-sm text-tinta-2 mb-2">Cargadas {cfg.mesas.filter((m) => m.tipo === "titular").length} titulares y{" "}
                {cfg.mesas.filter((m) => m.tipo === "reserva").length} reservas · universo {cfg.options.total_mesas || "sin definir"} mesas.</p>
            : <InfoBanner message="Falta cargar la muestra: un CSV con las columnas mesa, tipo (TITULAR, reserva o padron), reserva_de, local, macrozona y sector. Las filas «padron» dan local y sector a las mesas que lleguen fuera de la muestra." />}
          {cfg.puede_configurar && (
            <div className="flex flex-wrap items-end gap-3 mt-2">
              <div>
                <label className="etiqueta">CSV de la muestra</label>
                <input type="file" accept=".csv,text/csv" className="input"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void f.text().then((t) => leerCsv.mutate(t)); }} />
              </div>
              <div>
                <label className="etiqueta">Total de mesas del universo (N)</label>
                <input className="input w-32" inputMode="numeric" placeholder={String(cfg.options.total_mesas || "")}
                  value={totalMesas} onChange={(e) => setTotalMesas(e.target.value.replace(/\D/g, ""))} />
              </div>
              <button className="btn" disabled={guardar.isPending || (!previa && !totalMesas)}
                onClick={() => guardar.mutate({
                  ...(totalMesas ? { options: { total_mesas: Number(totalMesas) } } : {}),
                  ...(previa ? { mesas: previa.mesas } : {}),
                })}>
                {guardar.isPending ? <Spinner /> : "Guardar"}
              </button>
              {leerCsv.isPending && <Spinner />}
              {previa && <span className="text-sm text-tinta-2">Vista previa: {previa.titulares} titulares y {previa.reservas} reservas.</span>}
            </div>
          )}
          {errorCsv && <ErrorBanner message={errorCsv} />}
          {guardar.error && <ErrorBanner message={(guardar.error as Error).message} />}
        </Card>
      )}

      {listo && configurado && (
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="etiqueta">Actualización</label>
            <select className="input" value={refresco} onChange={(e) => setRefresco(Number(e.target.value))}>
              {REFRESCO.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="etiqueta">Actas</label>
            <select className="input" value={modo} onChange={(e) => setModo(e.target.value as "muestra" | "todas")}>
              <option value="muestra">Solo la muestra (probabilística)</option>
              <option value="todas">Todas las actas completas</option>
            </select>
          </div>
          <div>
            <label className="etiqueta">Porcentajes sobre</label>
            <select className="input" value={base} onChange={(e) => setBase(e.target.value as "validos" | "emitidos")}>
              <option value="validos">Votos válidos</option>
              <option value="emitidos">Votos emitidos</option>
            </select>
          </div>
          <button className="btn" onClick={() => leerAhora.mutate()} disabled={leerAhora.isPending}>
            {leerAhora.isPending ? <Spinner /> : "Leer ODK ahora"}
          </button>
          {res && <span className="text-xs text-tinta-3">Calculado {res.resumen.calculado} · el servidor relee ODK como máximo cada {res.cache_segundos} s</span>}
          {resQ.isFetching && !leerAhora.isPending && <Spinner />}
        </div>
      )}
      {resQ.error && <ErrorBanner message={(resQ.error as Error).message} />}
      {leerAhora.error && <ErrorBanner message={(leerAhora.error as Error).message} />}

      {res && vista && (
        <>
          {modo === "todas" && (
            <InfoBanner message={`Todas las actas: ${res.resumen.todas_actas} actas completas (muestra y fuera de ella, sin las de menos de ${res.resumen.min_votos} votos). No es una muestra probabilística: sirve como referencia y el margen es solo orientativo.`} />
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {modo === "muestra"
              ? <Tile titulo="Actas de la muestra" valor={`${res.resumen.actas} / ${res.resumen.titulares}`} nota={`${res.resumen.avance ?? 0} % de la muestra`} />
              : <Tile titulo="Actas completas (todas)" valor={`${res.resumen.todas_actas}`} nota={`${res.resumen.fuera_de_muestra} recibidas fuera de la muestra`} />}
            <Tile titulo="Votos contados" valor={(modo === "todas" ? res.resumen.todas_votos : res.resumen.votos).toLocaleString()} nota={res.resumen.total_mesas ? `Universo: ${res.resumen.total_mesas} mesas` : undefined} />
            <Tile titulo={dif ? `${dif.a} − ${dif.b}` : "Diferencia"}
              valor={dif?.diferencia_validos != null ? `${dif.diferencia_validos > 0 ? "+" : ""}${dif.diferencia_validos.toFixed(1)} pts` : "—"}
              nota={dif ? (dif.margen != null ? `±${dif.margen.toFixed(1)} · ${dif.empate_tecnico ? "empate técnico" : "diferencia significativa"}` : "faltan actas para el margen") : undefined} />
            <Tile titulo="Avisos" valor={`${res.resumen.sospechosas} sospechosas`}
              nota={modo === "muestra" ? `${res.resumen.fuera_de_muestra} mesas fuera de la muestra (${res.resumen.votos_fuera_de_muestra} votos, no entran aquí)` : `Incompletas (< ${res.resumen.min_votos} votos) excluidas`} />
          </div>

          {modo === "muestra" && res.resumen.actas < 30 && (
            <InfoBanner message={`Con ${res.resumen.actas} actas la estimación todavía es frágil: hacen falta unas 30, repartidas por todas las zonas.`} />
          )}

          <div className="grid md:grid-cols-3 gap-4">
            <Card className="md:col-span-2">
              <div className="etiqueta mb-2">Estimación ({base === "validos" ? "votos válidos" : "votos emitidos"}, margen al 95 %)</div>
              <GraficoPartidos res={vista} base={base} />
            </Card>
            <Card>
              <div className="etiqueta mb-2">Cobertura por zona</div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-tinta-3"><th>Zona</th><th className="text-right">Actas</th><th className="text-right">%</th></tr></thead>
                <tbody>
                  {vista.zonas.map((z) => (
                    <tr key={z.zona} className="border-t border-grid">
                      <td>{z.zona}</td><td className="text-right">{modo === "muestra" ? `${z.recibidas} / ${z.titulares}` : z.recibidas}</td>
                      <td className={`text-right ${modo === "muestra" && z.titulares && z.recibidas / z.titulares < 0.25 ? "text-estado-critico" : ""}`}>
                        {modo === "muestra" ? `${z.titulares ? Math.round((100 * z.recibidas) / z.titulares) : 0} %` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          <Card>
            <div className="etiqueta mb-2">Tabla de resultados</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-tinta-3"><th>Partido</th><th className="text-right">Votos</th><th className="text-right">% emitidos</th><th className="text-right">% válidos</th></tr></thead>
                <tbody>
                  {vista.resultados.filter((f) => f.votos > 0).map((f) => (
                    <tr key={f.codigo} className="border-t border-grid">
                      <td>{f.partido}</td><td className="text-right">{f.votos.toLocaleString()}</td>
                      <td className="text-right">{pct(f.pct_emitidos)}<span className="text-tinta-3">{mg(f.me_emitidos)}</span></td>
                      <td className="text-right">{pct(f.pct_validos)}<span className="text-tinta-3">{mg(f.me_validos)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {vista.sectores.length > 0 && (
            <Card>
              <div className="etiqueta mb-2">Por sector (votos válidos)</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-tinta-3"><th>Zona</th><th>Sector</th><th className="text-right">Actas</th>
                    {dif && <><th className="text-right">{pa}</th><th className="text-right">{pb}</th><th className="text-right">Diferencia</th></>}</tr></thead>
                  <tbody>
                    {vista.sectores.map((s) => {
                      const a = s.pct[pa] ?? null; const b = s.pct[pb] ?? null;
                      return (
                        <tr key={`${s.zona}-${s.sector}`} className="border-t border-grid">
                          <td>{s.zona}</td><td>{s.sector}</td><td className="text-right">{s.actas}</td>
                          {dif && <><td className="text-right">{pct(a)}</td><td className="text-right">{pct(b)}</td>
                            <td className="text-right">{a !== null && b !== null ? `${a - b > 0 ? "+" : ""}${(a - b).toFixed(1)}` : "—"}</td></>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {vista.evolucion.length >= 2 && (
            <Card>
              <div className="etiqueta mb-2">Cómo se movió la estimación según llegaban las actas (votos válidos)</div>
              <GraficoEvolucion res={vista} />
            </Card>
          )}

          <Card>
            <div className="etiqueta mb-2">Actas recibidas ({res.mesas.length}, muestra y fuera de ella)</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-tinta-3">
                    <th>Hora</th><th>Mesa</th><th>Muestra</th><th>Local</th><th>Zona</th><th>Sector</th><th>Fuente</th><th className="text-right">Total</th>
                    {dif && <><th className="text-right">{pa}</th><th className="text-right">{pb}</th></>}<th>Foto</th>
                  </tr>
                </thead>
                <tbody>
                  {mesasOrdenadas.map((m) => (
                    <tr key={m.mesa} className={`border-t border-grid ${m.sospechosa ? "text-estado-critico" : ""}`}>
                      <td>{m.hora ?? "—"}</td><td>{m.mesa}{m.tipo === "reserva" ? " (reserva)" : ""}</td><td>{m.en_muestra ? "Sí" : "No"}</td><td>{m.local}</td><td>{m.zona}</td><td>{m.sector}</td>
                      <td>{FUENTE[m.fuente] ?? m.fuente}</td>
                      <td className="text-right">{m.total}{m.sospechosa ? " ⚠" : ""}</td>
                      {dif && <><td className="text-right">{String(m[pa] ?? "")}</td><td className="text-right">{String(m[pb] ?? "")}</td></>}
                      <td>{m.foto ? "📷" : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <button className="btn" onClick={() => setVerPendientes((v) => !v)}>
              {verPendientes ? "Ocultar" : "Ver"} mesas titulares pendientes ({res.pendientes.length})
            </button>
            {verPendientes && (
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-tinta-3"><th>Mesa</th><th>Local</th><th>Zona</th></tr></thead>
                  <tbody>{res.pendientes.map((p) => <tr key={p.mesa} className="border-t border-grid"><td>{p.mesa}</td><td>{p.local}</td><td>{p.zona}</td></tr>)}</tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
